import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import StyledInput from "@/components/styled/StyledInput";
import { AutocompleteSearch } from "@/components/pendingShipments/autocompleteSearch";
import { CustomCotationFormData } from "@/types/forms/home/customCotationsForm.form";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import { GetAllCompanyDto } from "@/types/api/api-types";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import { useEffect } from "react";
import clsx from "clsx";
import { ControlledCountryComboboxCountryCode } from "../ui/customs/controlled-combobox-countryCode";
import { CountryCode } from "libphonenumber-js";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";

interface InfoPickupProps {
  company?: GetAllCompanyDto;
}

export const InfoPickup = ({ company }: InfoPickupProps) => {
  const { t } = useTranslation("boQuotations");
  const methods = useFormContext<CustomCotationFormData>();
  const manualPickup = methods.watch("pickupInfo.manualPickup");
  const pickupCountry = methods.watch("pickupInfo.pickupCountry");

  const defaultCountry: CountryCode = (pickupCountry || "FR") as CountryCode;

  useEffect(() => {
    if (manualPickup) {
      methods.setValue("pickupInfo.pickupLocationId", "");
    } else {
      methods.setValue("pickupInfo.pickupCountry", "");
      methods.setValue("pickupInfo.pickupStreet", "");
      methods.setValue("pickupInfo.pickupPostalCode", "");
      methods.setValue("pickupInfo.pickupCity", "");
      methods.setValue("pickupInfo.pickupPhone", "");
      methods.setValue("pickupInfo.pickupCompany", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPickup]);

  const handlePickupCountryChange = () => {
    methods.setValue("pickupInfo.pickupStreet", "");
    methods.setValue("pickupInfo.pickupPostalCode", "");
    methods.setValue("pickupInfo.pickupCity", "");
    methods.setValue("pickupInfo.pickupPhone", "");
  };

  const handlePlaceClicked = (place: google.maps.places.PlaceResult | null) => {
    const parsed = parseGooglePlaceAddress(place, pickupCountry);
    if (!parsed) return;

    methods.setValue("pickupInfo.pickupStreet", parsed.street);
    methods.setValue("pickupInfo.pickupPostalCode", parsed.postalCode);
    methods.setValue("pickupInfo.pickupCity", parsed.city);
    methods.trigger([
      "pickupInfo.pickupStreet",
      "pickupInfo.pickupPostalCode",
      "pickupInfo.pickupCity",
      "pickupInfo.pickupCountry",
    ]);
  };

  const handleStreetInput = (e: string) => {
    methods.setValue("pickupInfo.pickupStreet", e);
    methods.trigger(["pickupInfo.pickupStreet"]);
  };

  return (
    <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex w-full flex-col items-center justify-center gap-4">
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <h2 className="text-brand700 text-xl font-semibold">{t("pickupInfo.title")}</h2>
        </div>
        <div className="flex w-full flex-col gap-2">
          <ControlledSelectV2
            control={methods.control}
            name="pickupInfo.pickupLocationId"
            options={
              company?.pickUpLocations.map((pickUpLocation) => ({
                value: pickUpLocation.id,
                label: `${pickUpLocation.name} ${pickUpLocation.street} ${pickUpLocation.postalCode} ${pickUpLocation.city}`,
              })) || []
            }
            placeholder={t("pickupInfo.pickupLocationPlaceholder")}
            isDisabled={manualPickup}
          />
        </div>
        <div className="flex w-full flex-row items-center gap-4">
          <StyledSwitch control={methods.control} name="pickupInfo.manualPickup" />
          <span className="text-gray-800">{t("pickupInfo.manualPickupLabel")}</span>
        </div>
        <div
          className={clsx("flex w-full flex-col gap-2", {
            hidden: !manualPickup,
          })}
        >
          <div className="flex w-full flex-col gap-2 sm:flex-row">
            <StyledInput
              control={methods.control}
              name="pickupInfo.pickupLastName"
              title={t("pickupInfo.lastNameLabel")}
              className="w-full"
            />
            <StyledInput
              control={methods.control}
              name="pickupInfo.pickupFirstName"
              title={t("pickupInfo.firstNameLabel")}
              className="w-full"
            />
          </div>
          <StyledInput
            control={methods.control}
            name="pickupInfo.pickupCompany"
            title={t("pickupInfo.companyLabel")}
          />
          <ControlledCountryComboboxCountryCode
            name="pickupInfo.pickupCountry"
            control={methods.control}
            placeholder={t("pickupInfo.countryPlaceholder")}
            wrapperClassName="pb-2"
            onChange={handlePickupCountryChange}
          />
          <AutocompleteSearch
            key={pickupCountry}
            placeholder={t("pickupInfo.streetPlaceholder")}
            onPlaceClicked={handlePlaceClicked}
            control={methods.control}
            handleStreetInput={handleStreetInput}
            formValueOverride="pickupInfo.pickupStreet"
            disabled={!pickupCountry}
            countryCode={pickupCountry ? [pickupCountry] : []}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <StyledInput
              className="w-full sm:w-[40%] md:w-[25%]"
              control={methods.control}
              name="pickupInfo.pickupPostalCode"
              title={t("pickupInfo.postalCodeLabel")}
              disabled={!pickupCountry}
            />
            <StyledInput
              className="flex-1"
              control={methods.control}
              name="pickupInfo.pickupCity"
              title={t("pickupInfo.cityLabel")}
              disabled={!pickupCountry}
            />
          </div>
          <StyledPhoneInput
            key={defaultCountry}
            control={methods.control}
            name="pickupInfo.pickupPhone"
            title={t("pickupInfo.phoneLabel")}
            disabled={!pickupCountry}
            defaultCountry={defaultCountry}
          />
        </div>
      </div>
    </div>
  );
};
